/**
 * Processing Transcript Modal
 *
 * Shown when a call ends but the transcript is not yet available.
 * Collapsible — the user can dismiss the dialog while still waiting
 * for the summary_created event to trigger the call detail form.
 */

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import PhoneIcon from '@mui/icons-material/Phone';

import { Modal } from '@/shared/components';
import { useToast } from '@/shared/components/toasts';

import { useMakeNextCall } from '../../application/hooks';

// ============================================================================
// Types
// ============================================================================

interface ProcessingTranscriptModalProps {
  open: boolean;
  onClose: () => void;
  onProceed: () => void;
  isWorking?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function ProcessingTranscriptModal({ open, onClose, onProceed, isWorking = false }: ProcessingTranscriptModalProps) {
  const toast = useToast();
  const { mutate: makeNextCall } = useMakeNextCall();

  const handleProceed = () => {
    onProceed();
    makeNextCall(undefined, {
      onError: error => {
        console.error('Failed to proceed to next call:', error);
        toast.error('Failed to proceed to next call');
      },
    });
  };

  const actions = isWorking
    ? [{ label: 'Proceed to next Call', onClick: handleProceed, variant: 'contained' as const, color: 'primary' as const }]
    : [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Waiting for Transcription"
      icon={PhoneIcon}
      iconColor="info"
      maxWidth="xs"
      fullWidth
      actionsAlign="center"
      actions={actions}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          gap: 3,
        }}
      >
        <CircularProgress size={48} />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Waiting for Transcription
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The call transcript is being processed. The call detail form will appear automatically when ready.
          </Typography>
        </Box>
      </Box>
    </Modal>
  );
}
